import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Spinner } from 'admin-components/components';
import { historyShape, userShape, locationShape } from 'constants/propTypes';
import { Row, Column } from 'components/shared/Layout';
import { parse } from 'query-string';
import { isNil } from 'ramda';

import { useMutation } from '@apollo/client';
import { useManagedQuery } from 'utils/managedHooks';
import { REDEEM_VOUCHER, CANCEL_SPECIFIC_BOOKING_ITEMS, CONFIRM_BOOKING } from 'store/mutations';

import { GET_BOOKING } from 'store/queries';

import SplitPayment from '../../SplitPayment';
import { mapVoucherCurrencyToMarket, mapVoyagerToEvoucherChannels } from '../../Payments.utils';
import { handlePaymentSubmit } from '../../SplitPayment/SplitPayment.utils';
import { Errors } from '../../SplitPayment/SplitPayment.styles';
import VoucherForm from '../VoucherPayment/VoucherForm';
import VoucherDetails from '../VoucherPayment/VoucherDetails';

const EVoucherCancelPayment = ({
  totalPriceToBePaid,
  isOnHold,
  isCancelVoucherRoute,
  payAndConfirm,
  takeDeposit,
  addPayment,
  reference,
  currency,
  method,
  loading,
  user,
  history,
  location
}) => {
  const [redeemVoucher, { loading: isRedeemVoucherLoading, error: redeemVoucherError }] = useMutation(REDEEM_VOUCHER);
  const [voucherDetails, setVoucherDetails] = useState(null);
  const [cancelSpecificBookingItems] = useMutation(CANCEL_SPECIFIC_BOOKING_ITEMS);
  const [confirmBooking] = useMutation(CONFIRM_BOOKING);
  const { data } = useManagedQuery(GET_BOOKING, {
    variables: { reference }
  });

  const channel = mapVoyagerToEvoucherChannels(user.salesChannel);
  const queryObject = parse(location.search);
  const shouldSendRedeemEmail = isNil(queryObject.sendRedeemEmail) ? true : queryObject.sendRedeemEmail === 'true';

  if (isRedeemVoucherLoading) {
    return (
      <Row height="100vh">
        <Column alignItems="center" justifyContent="center">
          <Spinner />
        </Column>
      </Row>
    );
  }

  // eslint-disable-next-line consistent-return
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const {
        data: { redeemVoucher: { amountRedeemed, datacashReference, voucher } = {} }
      } = await redeemVoucher({
        variables: {
          reference: voucherDetails.voucher.reference,
          market: mapVoucherCurrencyToMarket[currency],
          channel,
          bookingReference: reference,
          cid: `VOY-EvoucherCancelRedeem-payment-gateway-${reference}`,
          sendRedeemEmail: shouldSendRedeemEmail,
          isDeposit: false
        }
      });

      await handlePaymentSubmit({
        cashTendered: amountRedeemed,
        totalPriceToBePaid,
        payAndConfirm,
        takeDeposit,
        addPayment,
        isOnHold,
        bookingReference: reference,
        currency,
        method,
        reference: datacashReference,
        externalReference: voucher.alias
      });

      await cancelSpecificBookingItems({
        variables: {
          bookingReference: data.booking.reference,
          itemRefs: data.booking.outboundExtras.map((item) => item.itemRef)
        }
      });

      await confirmBooking({
        variables: {
          bookingReference: data.booking.reference,
          sendCustomerEmail: !isCancelVoucherRoute
        }
      });

      if (Math.abs(data.booking.totalPriceToBePaid) > 0) {
        return history.push(`/manage/bookings/${data.booking.reference}/refund-methods?cancel-voucher&currency=${currency}`);
      }
    } catch (err) {
      return err;
    }
  };

  return (
    <SplitPayment
      title={<FormattedMessage id="payments.AmountToPayByEvoucher" />}
      subTitle={<FormattedMessage id="payments.canBeSplit" />}
      label={<FormattedMessage id="payments.recordPayment" />}
      loading={loading}
      onSubmit={handleSubmit}
      hideSubmit={voucherDetails === null}
    >
      {!voucherDetails ? (
        <VoucherForm bookingReference={reference} onSubmit={setVoucherDetails} resetVoucher={() => setVoucherDetails(null)} />
      ) : (
        <>
          <VoucherDetails
            currency={currency}
            amount={voucherDetails.voucher.balance}
            redeemAmount={Math.min(voucherDetails.voucher.balance, parseFloat(totalPriceToBePaid))}
            voucherCode={voucherDetails.voucher.reference}
            resetVoucher={() => setVoucherDetails(null)}
          />
          <Errors>{redeemVoucherError && <div data-cy="redeem-error-message">{redeemVoucherError.message}</div>}</Errors>
        </>
      )}

      <Errors>{redeemVoucherError && <div>{redeemVoucherError.message}</div>}</Errors>
    </SplitPayment>
  );
};

EVoucherCancelPayment.propTypes = {
  totalPriceToBePaid: PropTypes.number,
  loading: PropTypes.bool,
  isOnHold: PropTypes.bool,
  isCancelVoucherRoute: PropTypes.bool,
  payAndConfirm: PropTypes.func,
  takeDeposit: PropTypes.func,
  addPayment: PropTypes.func,
  reference: PropTypes.string,
  currency: PropTypes.string,
  method: PropTypes.string,
  user: userShape,
  history: historyShape,
  location: locationShape
};

export default EVoucherCancelPayment;
